import {
  TournamentGender,
  TournamentType,
  TournamentTypeKeys,
} from '../enums/enums';
import {
  CreateEventSportsDto,
  CustomCategoryDto,
  DateFieldKey,
  EventBasicDetails,
  EventCreateRequest,
  EventFormState,
  EventGendersDto,
  GameScore,
  GenderKey,
  SetScore,
  SimpleSetScore,
  TennisScore,
  TennisSet,
  TournamentsBySport,
  TournamentSelection,
} from '../types/types';
import dayjs from 'dayjs';

const GenderKeyToEnum: Record<string, TournamentGender> = {
  male: TournamentGender.Male,
  female: TournamentGender.Female,
  mixed: TournamentGender.Mixed,
};

const mapFormToApiPayload = (formState: EventFormState): EventCreateRequest => {
  const eventBasicDetails: EventBasicDetails = {
    eventName: formState.eventName || null,
    eventDescription: formState.description || null,
    eventStartDate: formState.eventStartDate
      ? dayjs(formState.eventStartDate).startOf('day').toISOString()
      : '',
    eventEndDate: formState.eventEndDate
      ? dayjs(formState.eventEndDate).endOf('day').toISOString()
      : '',
    registrationStartDate: formState.registrationStartDate
      ? dayjs(formState.registrationStartDate).startOf('day').toISOString()
      : null,
    registrationEndDate: formState.registrationEndDate
      ? dayjs(formState.registrationEndDate).endOf('day').toISOString()
      : null,
    eventLocation: formState.eventAddress,
    registrationFee: formState.registrationFee,
    eventLocationLatitude: formState.eventLat,
    eventLocationLongitude: formState.eventLng,
    eventOrganizerUserId: formState.eventOrganizerUserId || null,
  };

  const eventSports = mapTournamentSelectionToEventSports(
    formState.tournaments,
  );

  return {
    eventBasicDetails,
    eventSports: eventSports,
  };
};

const mapTournamentSelectionToEventSports = (
  selection: TournamentSelection,
): CreateEventSportsDto[] => {
  const result: CreateEventSportsDto[] = [];
  for (const sportIdStr of Object.keys(selection)) {
    const sportId = Number(sportIdStr);
    const gendersObj = selection[sportId];

    const eventGenders: EventGendersDto[] = [];

    for (const genderKey of Object.keys(gendersObj)) {
      const categories = gendersObj[genderKey as keyof typeof gendersObj];

      if (!categories) continue;

      const customCategories: CustomCategoryDto[] = [];

      for (const categoryIdStr of Object.keys(categories)) {
        const categoryId = categoryIdStr;

        const types = categories[categoryId];
        if (!types || types.length === 0) continue;

        customCategories.push({
          customCategoryName: String(categoryId),
          tournamentTypes: types as TournamentType[],
        });
      }

      if (customCategories.length > 0) {
        eventGenders.push({
          gender: GenderKeyToEnum[genderKey],
          customCategories,
        });
      }
    }

    if (eventGenders.length > 0) {
      result.push({
        sportId,
        eventGenders,
      });
    }
  }

  return result;
};

// Validation functions for each step
const validateBasicInfo = (formState: EventFormState) => {
  const newErrors: Record<string, string> = {};

  if (!formState.eventName?.trim()) {
    newErrors.eventName = 'Event name is required';
  }

  if (!formState.description?.trim()) {
    newErrors.description = 'Description is required';
  }

  if (!formState.eventImage) {
    newErrors.eventImage = 'Event image is required';
  }

  if (!formState.registrationStartDate) {
    newErrors.registrationStartDate = 'Registration start date is required';
  }

  if (!formState.registrationEndDate) {
    newErrors.registrationEndDate = 'Registration end date is required';
  }

  if (!formState.eventStartDate) {
    newErrors.eventStartDate = 'Event start date is required';
  }

  if (!formState.eventEndDate) {
    newErrors.eventEndDate = 'Event end date is required';
  }

  if (!formState.eventAddress?.trim()) {
    newErrors.eventAddress = 'Event address is required';
  }

  // Date validation
  if (formState.registrationStartDate && formState.registrationEndDate) {
    if (
      formState.registrationStartDate.isAfter(formState.registrationEndDate)
    ) {
      newErrors.registrationEndDate =
        'Registration end date must be after start date';
    }
  }

  if (formState.eventStartDate && formState.eventEndDate) {
    if (formState.eventStartDate.isAfter(formState.eventEndDate)) {
      newErrors.eventEndDate = 'Event end date must be after start date';
    }
  }

  if (formState.registrationEndDate && formState.eventStartDate) {
    if (formState.registrationEndDate.isAfter(formState.eventStartDate)) {
      newErrors.eventStartDate =
        'Event start date must be after registration end date';
    }
  }

  if (
    formState.registrationFee === null ||
    formState.registrationFee === undefined ||
    formState.registrationFee < 0
  ) {
    newErrors.registrationFee = 'Registration fee is required';
  }

  return newErrors;
};

const validateSports = (formState: EventFormState) => {
  const newErrors: Record<string, string> = {};

  if (!formState.selectedSports || formState.selectedSports.length === 0) {
    newErrors.selectedSports = 'Please select at least one sport';
  }

  return newErrors;
};

const validateCategories = (formState: EventFormState) => {
  const newErrors: Record<string, string> = {};

  // Check if any tournaments are configured
  let hasTournaments = false;

  if (formState.tournaments) {
    // Handle tournaments as Record<number, TournamentsBySport>
    const tournamentsRecord = formState.tournaments as unknown as Record<
      number,
      TournamentsBySport
    >;

    for (const sportIdStr of Object.keys(tournamentsRecord)) {
      const sportId = parseInt(sportIdStr);
      const sport = tournamentsRecord[sportId];
      if (sport) {
        const genders: ('male' | 'female' | 'mixed')[] = [
          'male',
          'female',
          'mixed',
        ];
        for (const gender of genders) {
          const genderObj = sport[gender];
          if (genderObj && typeof genderObj === 'object') {
            for (const category of Object.keys(genderObj)) {
              const types = genderObj[category];
              if (Array.isArray(types) && types.length > 0) {
                hasTournaments = true;
                break;
              }
            }
            if (hasTournaments) break;
          }
        }
        if (hasTournaments) break;
      }
    }
  }

  if (!hasTournaments) {
    newErrors.tournaments = 'Please configure at least one tournament';
  }

  return newErrors;
};

const dateFieldGroups: { label: string; key: DateFieldKey }[][] = [
  [
    { label: 'Registration Start', key: 'registrationStartDate' },
    { label: 'Registration End', key: 'registrationEndDate' },
  ],
  [
    { label: 'Event Start', key: 'eventStartDate' },
    { label: 'Event End', key: 'eventEndDate' },
  ],
];

const startOfDayFields: DateFieldKey[] = [
  'eventStartDate',
  'registrationStartDate',
];

const TYPES_BY_GENDER: Record<GenderKey, number[]> = {
  female: [TournamentType.Singles, TournamentType.Doubles],
  male: [TournamentType.Singles, TournamentType.Doubles],
  mixed: [TournamentType.MixedDoubles],
};

const typeLabelMap: Record<number, string> = {
  [TournamentType.Singles]: 'Singles',
  [TournamentType.Doubles]: 'Doubles',
  [TournamentType.MixedDoubles]: 'Mixed Doubles',
  [TournamentType.Team]: 'Team',
};

const TournamentTypeMap: Record<TournamentType, TournamentTypeKeys> = {
  [TournamentType.Singles]: TournamentTypeKeys.Singles,
  [TournamentType.Doubles]: TournamentTypeKeys.Doubles,
  [TournamentType.MixedDoubles]: TournamentTypeKeys.MixedDoubles,
  [TournamentType.Team]: TournamentTypeKeys.Team,
};

const defaultScores = (sport: string) => {
  switch (sport) {
    case 'Tennis':
      return {
        sets: [],
        currentSet: {
          games1: 0,
          games2: 0,
          tiebreakScore1: null,
          tiebreakScore2: null,
        },
      } as TennisScore;
    case 'Volleyball':
    case 'Throwball':
    case 'Badminton':
      return {
        sets: [],
        currentSetScore: 0,
      } as SimpleSetScore;
    case 'Table Tennis':
    case 'Pickleball':
      return {
        games: [],
        currentGameScore: 0,
      } as GameScore;
    default:
      return null;
  }
};

const parseScoreData = (data: string, sport: string) => {
  if (!data || data.trim() === '' || data.trim() === '{}') {
    return defaultScores(sport);
  }
  const jsonData = JSON.parse(data);
  switch (sport) {
    case 'Tennis':
      return {
        sets: (jsonData as TennisScore).sets.map((set) => set.games1),
        data: jsonData as TennisScore,
      };

    case 'Volleyball':
    case 'Throwball':
    case 'Badminton':
      return {
        sets: (jsonData as SimpleSetScore).sets,
        data: jsonData as SimpleSetScore,
      };

    case 'Table Tennis':
    case 'Pickleball':
      return {
        sets: (jsonData as GameScore).games,
        data: jsonData as GameScore,
      };

    default:
      return null;
  }
};

const buildScoreJsonFromSets = (updatedSets: SetScore[], sportName: string) => {
  const setGames = ['Volleyball', 'Throwball', 'Badminton'];
  const scoreGames = ['Table Tennis', 'Pickleball'];

  if (setGames.includes(sportName)) {
    return JSON.stringify({
      sets: updatedSets.map((s) => Number(s.value) || 0),
      currentSetScore: Number(updatedSets.at(-1)?.value) || 0,
    });
  }

  if (scoreGames.includes(sportName)) {
    return JSON.stringify({
      games: updatedSets.map((s) => Number(s.value) || 0),
      currentGameScore: Number(updatedSets.at(-1)?.value) || 0,
    });
  }

  if (sportName === 'Tennis') {
    return JSON.stringify({
      sets: updatedSets.map(
        (set) =>
          ({
            games1: Number(set.value) || 0,
            games2: 0,
            tiebreakScore1: null,
            tiebreakScore2: null,
          }) as TennisSet,
      ),
      currentGameScore: Number(updatedSets.at(-1)?.value) || 0,
    });
  }

  // Fail loudly rather than silently posting "{}" (which the API rejects with
  // "Scores must be entered"). Cricket uses a different scoring model and is not
  // supported by this set-based builder; an empty/unknown sport name lands here too.
  throw new Error(
    sportName
      ? `Score entry is not supported for ${sportName} on this screen.`
      : 'Unable to determine the sport for this match, so scores cannot be saved.',
  );
};

const isValidNumber = (v: string) => /^-?\d+$/.test(v.trim());

export {
  GenderKeyToEnum,
  mapFormToApiPayload,
  mapTournamentSelectionToEventSports,
  validateBasicInfo,
  validateSports,
  validateCategories,
  dateFieldGroups,
  startOfDayFields,
  TYPES_BY_GENDER,
  typeLabelMap,
  TournamentTypeMap,
  defaultScores,
  parseScoreData,
  buildScoreJsonFromSets,
  isValidNumber,
};
